/**
 * Agenda Personal Notes
 *
 * Allows users to add private notes to agenda items.
 * For authenticated users, notes are saved to the database.
 * For unauthenticated users, notes are stored in localStorage.
 *
 * @since 2.5.0
 */

/* global democraticAgendaNotes */

(function ($) {
	'use strict';

	// Check if user is logged in
	const isLoggedIn = document.body.classList.contains('logged-in');

	// LocalStorage key prefix
	const STORAGE_PREFIX = 'agenda_note_';

	/**
	 * Initialize notes system
	 */
	function initAgendaNotes() {
		if ($('.agenda-item-notes').length > 0) {
			loadLocalNotesForPublicUsers();
			attachNoteHandlers();
		}
	}

	/**
	 * Load notes from localStorage for public users
	 */
	function loadLocalNotesForPublicUsers() {
		if (isLoggedIn) {
			return; // Skip for logged-in users
		}

		$('.agenda-item-notes').each(function () {
			const $notesSection = $(this);
			const agendaId = $notesSection.data('agenda-id');
			const itemNumber = $notesSection.data('item-number');
			const storageKey = `${STORAGE_PREFIX}${agendaId}_${itemNumber}`;
			const savedNote = localStorage.getItem(storageKey);

			if (savedNote) {
				// Show the note
				showNoteDisplay($notesSection, savedNote);
			}
		});
	}

	/**
	 * Show note display section
	 */
	function showNoteDisplay($notesSection, noteText) {
		// Hide add button
		$notesSection.find('.add-note-btn').hide();

		// Create display if it doesn't exist
		let $display = $notesSection.find('.notes-display');
		if ($display.length === 0) {
			$display = $('<div class="notes-display bg-blue-50 border border-blue-200 rounded-lg p-3"></div>');
			$display.html(`
				<div class="flex items-start justify-between mb-2">
					<h5 class="text-sm font-medium text-blue-900 flex items-center">
						<svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
						</svg>
						Your Personal Note
					</h5>
					<div class="flex gap-2">
						<button class="edit-note-btn text-xs text-blue-600 hover:text-blue-700">Edit</button>
						<button class="delete-note-btn text-xs text-red-600 hover:text-red-700">Delete</button>
					</div>
				</div>
				<div class="user-note-content text-sm text-gray-700" data-note=""></div>
			`);
			$notesSection.prepend($display);
		}

		// Update content
		$display.find('.user-note-content').text(noteText).data('note', noteText);
		$display.show();
	}

	/**
	 * Attach event handlers
	 */
	function attachNoteHandlers() {
		// Show editor when clicking "Add Note"
		$(document).on('click', '.add-note-btn', function (e) {
			e.preventDefault();
			const $notesSection = $(this).closest('.agenda-item-notes');
			$notesSection.find('.notes-editor').slideDown();
			$(this).hide();
			$notesSection.find('.note-textarea').focus();
		});

		// Save note
		$(document).on('click', '.save-note-btn', function (e) {
			e.preventDefault();
			e.stopPropagation();

			const $btn = $(this);
			const $notesSection = $btn.closest('.agenda-item-notes');
			const $textarea = $notesSection.find('.note-textarea');
			const $status = $notesSection.find('.note-status');
			const itemNumber = $notesSection.data('item-number');
			const note = $textarea.val().trim();
			const agendaId = getAgendaId();

			if (!agendaId) {
				$status.html('<span style="color: #d63638;">Error: No agenda ID</span>');
				return;
			}

			// Disable button and show saving status
			$btn.prop('disabled', true).text('Saving...');
			$status.html('<span style="color: #666;">Saving...</span>');

			// Handle based on authentication status
			if (isLoggedIn) {
				// Save to database via AJAX
				saveToDatabase(agendaId, itemNumber, note, $btn, $status, $notesSection);
			} else {
				// Save to localStorage
				saveToLocalStorage(agendaId, itemNumber, note, $btn, $status, $notesSection);
			}
		});

		// Edit note
		$(document).on('click', '.edit-note-btn', function (e) {
			e.preventDefault();
			const $notesSection = $(this).closest('.agenda-item-notes');
			const currentNote = $notesSection.find('.user-note-content').data('note') || '';

			$notesSection.find('.note-textarea').val(currentNote);
			$notesSection.find('.notes-display').slideUp();
			$notesSection.find('.notes-editor').slideDown();
			$notesSection.find('.note-textarea').focus();
		});

		// Cancel editing
		$(document).on('click', '.cancel-note-btn', function (e) {
			e.preventDefault();
			const $notesSection = $(this).closest('.agenda-item-notes');
			const hasExistingNote = $notesSection.find('.user-note-content').data('note');

			$notesSection.find('.notes-editor').slideUp();
			$notesSection.find('.note-status').empty();

			if (hasExistingNote) {
				$notesSection.find('.notes-display').slideDown();
			} else {
				$notesSection.find('.add-note-btn').show();
			}
		});

		// Delete note
		$(document).on('click', '.delete-note-btn', function (e) {
			e.preventDefault();

			if (!confirm('Delete this note?')) {
				return;
			}

			const $notesSection = $(this).closest('.agenda-item-notes');
			const itemNumber = $notesSection.data('item-number');
			const agendaId = getAgendaId();

			if (!agendaId) {
				return;
			}

			// Handle based on authentication status
			if (isLoggedIn) {
				deleteFromDatabase(agendaId, itemNumber, $notesSection);
			} else {
				deleteFromLocalStorage(agendaId, itemNumber, $notesSection);
			}
		});
	}

	/**
	 * Save note to database (authenticated users)
	 */
	function saveToDatabase(agendaId, itemNumber, note, $btn, $status, $notesSection) {
		const ajaxData = {
			action: 'democratic_save_agenda_note',
			nonce: democraticAgendaNotes ? democraticAgendaNotes.nonce : '',
			agenda_id: agendaId,
			item_number: itemNumber,
			note: note,
		};

		$.ajax({
			url: democraticAgendaNotes ? democraticAgendaNotes.ajaxUrl : '/wp-admin/admin-ajax.php',
			type: 'POST',
			data: ajaxData,
			success: function (response) {
				if (response.success) {
					$status.html('<span style="color: #00a32a;">✓ Saved!</span>');

					// Update display
					if (note) {
						showNoteDisplay($notesSection, note);
						$notesSection.find('.notes-editor').slideUp();
					} else {
						$notesSection.find('.notes-display').slideUp();
						$notesSection.find('.notes-editor').slideUp();
						$notesSection.find('.add-note-btn').show();
					}

					setTimeout(function () {
						$status.empty();
					}, 3000);
				} else {
					const msg = (response.data && response.data.message) ? response.data.message : 'Note could not be saved.';
					$status.html(`<span style="color: #d63638;">${ msg }</span>`);
				}
			},
			error: function (xhr, status, err) {
				let msg = 'Note could not be saved. Please try again.';
				if (xhr && xhr.responseJSON && xhr.responseJSON.data && xhr.responseJSON.data.message) {
					msg = xhr.responseJSON.data.message;
				}
				$status.html(`<span style="color: #d63638;">${ msg }</span>`);
			},
			complete: function () {
				$btn.prop('disabled', false).text('Save');
			},
		});
	}

	/**
	 * Save note to localStorage (unauthenticated users)
	 */
	function saveToLocalStorage(agendaId, itemNumber, note, $btn, $status, $notesSection) {
		const storageKey = `${STORAGE_PREFIX}${agendaId}_${itemNumber}`;

		try {
			if (note) {
				localStorage.setItem(storageKey, note);
				$status.html('<span style="color: #00a32a;">✓ Saved locally!</span>');
				showNoteDisplay($notesSection, note);
				$notesSection.find('.notes-editor').slideUp();
			} else {
				localStorage.removeItem(storageKey);
				$status.html('<span style="color: #00a32a;">✓ Note removed</span>');
				$notesSection.find('.notes-display').slideUp();
				$notesSection.find('.notes-editor').slideUp();
				$notesSection.find('.add-note-btn').show();
			}

			setTimeout(function () {
				$status.empty();
			}, 3000);
		} catch (e) {
			$status.html('<span style="color: #d63638;">Error: Cannot save note locally</span>');
		} finally {
			$btn.prop('disabled', false).text('Save');
		}
	}

	/**
	 * Delete note from database (authenticated users)
	 */
	function deleteFromDatabase(agendaId, itemNumber, $notesSection) {
		$.ajax({
			url: democraticAgendaNotes ? democraticAgendaNotes.ajaxUrl : '/wp-admin/admin-ajax.php',
			type: 'POST',
			data: {
				action: 'democratic_delete_agenda_note',
				nonce: democraticAgendaNotes ? democraticAgendaNotes.nonce : '',
				agenda_id: agendaId,
				item_number: itemNumber,
			},
			success: function (response) {
				if (response.success) {
					$notesSection.find('.notes-display').slideUp();
					$notesSection.find('.notes-editor').slideUp();
					$notesSection.find('.note-textarea').val('');
					$notesSection.find('.user-note-content').text('').data('note', '');
					$notesSection.find('.add-note-btn').show();
				}
			},
		});
	}

	/**
	 * Delete note from localStorage (unauthenticated users)
	 */
	function deleteFromLocalStorage(agendaId, itemNumber, $notesSection) {
		const storageKey = `${STORAGE_PREFIX}${agendaId}_${itemNumber}`;
		localStorage.removeItem(storageKey);

		$notesSection.find('.notes-display').slideUp();
		$notesSection.find('.notes-editor').slideUp();
		$notesSection.find('.note-textarea').val('');
		$notesSection.find('.user-note-content').text('').data('note', '');
		$notesSection.find('.add-note-btn').show();
	}

	/**
	 * Get agenda ID from page
	 */
	function getAgendaId() {
		// Try from agenda notes section
		const $notes = $('.agenda-item-notes').first();
		if ($notes.length) {
			const id = $notes.data('agenda-id');
			if (id) {
				return parseInt(id);
			}
		}

		// Try from body class
		const bodyClasses = document.body.className;
		const match = bodyClasses.match(/postid-(\d+)/);
		if (match) {
			return parseInt(match[1]);
		}

		return 0;
	}

	// Initialize when document is ready
	$(document).ready(function () {
		initAgendaNotes();
	});
})(jQuery);
